import type { NpcHullTier } from "./types";
import { RICHNESS_MULTIPLIER, type Richness } from "./resourceCatalog";
import { bonusMultiplier } from "./captainSkills";

// Mining laser yield rate by Digger tier (16.25), solved backward from each
// tier's own Standard cargo against a shrinking fill-time target (20 -> 10
// -> 5 -> 2.5 min, halving each tier). Defined for all 4 hull tiers -- T3/T4
// Diggers still mine for their own hull/module construction needs directly
// even though they don't run mining MISSIONS (CLAUDE.md; 16.24's "removed
// for T3/T4" revision only affects the mission-board generator).
export const MINING_YIELD_RATE_M3_PER_S: Record<NpcHullTier, number> = {
  T1: 0.058,
  T2: 0.35,
  T3: 1.867,
  T4: 10.467
};

export type DangerTier = 0 | 1 | 2 | 3 | 4;

// Asteroid node "health" (total extractable m3 before depletion), by system
// Danger Tier (16.25) -- deliberately grows slower than cargo across tiers,
// reproducing the real "must node-hop at the top" effect. Richness (16.2)
// multiplies this base directly.
export const NODE_POOL_BASE_M3: Record<DangerTier, number> = {
  0: 150,
  1: 250,
  2: 450,
  3: 800,
  4: 1400
};

export function nodePoolM3(dangerTier: DangerTier, richness: Richness): number {
  return NODE_POOL_BASE_M3[dangerTier] * RICHNESS_MULTIPLIER[richness];
}

// Ambient asteroid-belt/planetary-pocket richness scales with a System's
// own Danger Tier (design doc 16.55/Section 8, LOCKED interview pass):
// Tier 0 = 1.0x baseline, Tier 4 = 2.0x -- "leave the Safe Zone for better
// resources" as a real ambient-mining incentive, not just a combat-risk
// dial. A distinct axis from the per-node `Richness` (Poor/Standard/Rich/
// Exceptional) quality tier above, which is unaffected -- multiply the two
// together at the call site (this is not folded into nodePoolM3 itself, to
// leave that function's existing locked behavior/callers unchanged).
export function dangerTierRichnessMultiplier(dangerTier: DangerTier): number {
  return 1.0 + dangerTier * 0.25;
}

// How many of a given node (before it depletes) it takes to fill a cargo
// quota -- 16.25's "must move between multiple nodes per session" effect,
// made concrete: >1 whenever the node pool is smaller than the quota.
export function nodesNeededToFillCargo(quotaM3: number, nodePoolM3Value: number): number {
  return Math.ceil(quotaM3 / nodePoolM3Value);
}

// Mining skill applies its flat +1%/level bonus to yield rate (16.31).
export function effectiveYieldRate(hullTier: NpcHullTier, miningSkillXp = 0): number {
  return MINING_YIELD_RATE_M3_PER_S[hullTier] * bonusMultiplier(miningSkillXp);
}

export function timeToMineSeconds(
  quotaM3: number,
  hullTier: NpcHullTier,
  miningSkillXp = 0
): number {
  return quotaM3 / effectiveYieldRate(hullTier, miningSkillXp);
}
